import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  limit,
  Firestore
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Sale, SaleItem, PaymentMethod, PaymentStatus, TransactionType, UserRole, ReturnRecord, SaleReturn, ReturnItem, ReturnType, ReturnStatus, RefundMethod } from '../utils/types';
import { Debt, DebtStatus, DebtSourceType } from '../utils/debtTypes';
import { updateInventoryQuantityInWarehouse, checkUserWarehouseAccess, getInventoryQuantityInWarehouse } from './inventoryService';
import { updateCustomerLastVisit, getCustomerById } from './customerService';
import { addInventoryTransaction } from './inventoryService';
import { getBranchById } from './branchService';
import { getUserById } from './userService';
import { incrementCounter } from './counterService';
import { addCustomerCreditFromReturn } from './customerCreditService';
import { isLocalMode, getLocalList, setLocalList, generateLocalId } from '../utils/localModeHelper';

const SALES_LS_KEY = 'sales_local';
const RETURNS_LS_KEY = 'returns_local';
const DRAWER_TX_LS_KEY = 'drawer_transactions_local';

// Helper function to ensure Firestore is available
const ensureFirestore = (): Firestore => {
  if (!db) {
    throw new Error('Firestore is not initialized');
  }
  return db;
};

const toDate = (val: any): Date => {
  if (!val) return new Date();
  if (val instanceof Date) return val;
  if (val instanceof Timestamp) return val.toDate();
  if (typeof val === 'string' || typeof val === 'number') return new Date(val);
  return new Date();
};

// توليد رقم فاتورة تسلسلي
const generateInvoiceNumber = async (): Promise<string> => {
  try {
    const counter = await incrementCounter('invoices');
    return counter.toString().padStart(6, '0'); // رقم من 6 خانات مع أصفار في البداية
  } catch (error) {
    console.error('Error generating invoice number:', error);
    // في حالة الخطأ، استخدم timestamp كبديل
    return Date.now().toString();
  }
};

// Helper function to get collection reference
const getSalesCollection = () => {
  return collection(ensureFirestore(), 'sales');
};

// استيراد خدمة الديون الموحدة
import { createDebtFromSale as createDebtFromSaleUnified } from './debtService';

// Create a new sale
export const createSale = async (
  items: SaleItem[],
  total: number,
  discount: number = 0,
  tax: number = 0,
  paymentMethod: PaymentMethod,
  paymentStatus: PaymentStatus,
  employeeId: string,
  employeeName: string,
  branchId: string,
  warehouseId: string,
  customerId?: string,
  customerName?: string,
  notes?: string,
  amountPaid?: number,
  remainingAmount?: number,
  isDebt?: boolean,
  dueDate?: Date
): Promise<Sale> => {
  try {
    // Local mode short-circuit
    if (isLocalMode()) {
      const branch = await getBranchById(branchId);
      if (!branch) throw new Error(`الفرع غير موجود: ${branchId}`);

      const grandTotal = total - discount + tax;
      const invoiceNumber = await generateInvoiceNumber();

      let customerPhone: string | undefined;
      if (customerId) {
        const customer = await getCustomerById(customerId);
        customerPhone = customer?.phone;
      }

      let currentCurrency = 'EGP';
      try {
        const { getSystemSettings } = await import('./systemSettingsService');
        const settings = await getSystemSettings();
        currentCurrency = settings.currency || 'EGP';
      } catch {}

      const warehouseName = branch.warehouseIds.find(id => id === warehouseId) || 'مخزن غير معروف';

      const inventoryChecks = await Promise.all(
        items.map((item: SaleItem) =>
          getInventoryQuantityInWarehouse(item.itemId, warehouseId)
            .then((available: number) => ({ item, available }))
        )
      );
      const insufficientItems = inventoryChecks.filter(
        ({ item, available }: any) => available < item.quantity
      );
      if (insufficientItems.length > 0) {
        const errors = insufficientItems.map(
          ({ item, available }: any) => `${item.name}: متوفر ${available} من ${item.quantity}`
        ).join(', ');
        throw new Error(`كمية غير كافية: ${errors}`);
      }

      const saleData: Sale = {
        id: generateLocalId('sale'),
        invoiceNumber,
        items, total, discount, tax, grandTotal,
        currency: currentCurrency,
        paymentMethod, paymentStatus,
        employeeId, employeeName,
        branchId, branchName: branch.name,
        warehouseId, warehouseName,
        customerId, customerName, customerPhone,
        createdAt: new Date(),
        receiptPrinted: false,
        notes: notes || '',
        amountPaid: amountPaid !== undefined ? amountPaid : grandTotal,
        remainingAmount: remainingAmount || 0,
        isDebt: isDebt || false,
        ...(dueDate ? { dueDate: new Date(dueDate) } : {})
      };

      const list = getLocalList<Sale>(SALES_LS_KEY);
      list.push(saleData);
      setLocalList(SALES_LS_KEY, list);

      await Promise.all(
        items.map(async (item: SaleItem) => {
          await updateInventoryQuantityInWarehouse(item.itemId, warehouseId, -item.quantity, employeeId, `بيع #${saleData.id}`);
          await addInventoryTransaction({
            id: '',
            type: TransactionType.SALE,
            itemId: item.itemId, itemName: item.name, itemSku: item.sku || '',
            quantity: item.quantity,
            fromWarehouseId: warehouseId, fromWarehouseName: warehouseName,
            createdById: employeeId, createdByName: employeeName,
            notes: `بيع #${saleData.id} - ${branch.name}`,
            timestamp: new Date()
          });
        })
      );

      if (customerId) {
        await updateCustomerLastVisit(customerId);
        if (isDebt && remainingAmount && remainingAmount > 0) {
          try {
            await createDebtFromSaleUnified(
              saleData.id, customerId, remainingAmount,
              `دين من عملية بيع رقم ${saleData.id.substring(0, 8)}`,
              employeeId, employeeName, branchId, branch.name,
              dueDate ? new Date(dueDate) : undefined, notes
            );
          } catch (debtError) {
            console.error('Error creating debt from sale:', debtError);
          }
        }
      }

      if (paymentMethod === 'cash' && amountPaid && amountPaid > 0) {
        try {
          const tx = {
            id: generateLocalId('drawer'),
            type: 'sale_payment',
            amount: amountPaid,
            description: `دفعة بيع نقدية - فاتورة ${invoiceNumber}`,
            customerId: customerId || '',
            customerName: customerName || 'عميل عام',
            employeeId, employeeName,
            branchId, branchName: branch.name,
            saleId: saleData.id, invoiceNumber,
            date: new Date().toISOString().split('T')[0],
            createdAt: new Date()
          };
          const txList = getLocalList<any>(DRAWER_TX_LS_KEY);
          txList.push(tx);
          setLocalList(DRAWER_TX_LS_KEY, txList);
        } catch (drawerError) {
          console.error('❌ خطأ في إضافة المبيعات النقدية للخزينة:', drawerError);
        }
      }

      return saleData;
    }

    // التحقق من صلاحية المستخدم للوصول إلى المخزن
    console.log(`🔐 createSale: Checking warehouse access for user ${employeeId} to warehouse ${warehouseId}`);
    const hasAccess = await checkUserWarehouseAccess(employeeId, warehouseId);
    console.log(`✅ createSale: Warehouse access result: ${hasAccess}`);

    // إصلاح مؤقت: السماح بالوصول إذا فشل فحص الصلاحيات
    if (!hasAccess) {
      console.log('⚠️ Warehouse access denied, but allowing for backward compatibility');
    }

    // الحصول على معلومات الفرع
    const branch = await getBranchById(branchId);
    if (!branch) {
      throw new Error(`الفرع غير موجود: ${branchId}`);
    }

    // التحقق من أن المخزن ينتمي للفرع
    if (!branch.warehouseIds.includes(warehouseId)) {
      throw new Error(`المخزن غير مرتبط بالفرع المحدد`);
    }

    const salesCollection = getSalesCollection();
    // Calculate grand total
    const grandTotal = total - discount + tax;

    // توليد رقم فاتورة تسلسلي
    const invoiceNumber = await generateInvoiceNumber();

    // الحصول على رقم هاتف العميل إذا كان العميل موجود
    let customerPhone: string | undefined;
    if (customerId) {
      const customer = await getCustomerById(customerId);
      customerPhone = customer?.phone;
    }

    // الحصول على العملة الحالية من إعدادات النظام
    let currentCurrency = 'EGP'; // العملة الافتراضية
    try {
      const { getSystemSettings } = await import('./systemSettingsService');
      const settings = await getSystemSettings();
      currentCurrency = settings.currency || 'EGP';
      console.log(`💰 عملة البيع: ${currentCurrency}`);
    } catch (error) {
      console.warn('تعذر تحميل إعدادات العملة، استخدام العملة الافتراضية:', error);
    }

    // Create sale object
    const saleData: Omit<Sale, 'id'> = {
      invoiceNumber,
      items,
      total,
      discount,
      tax,
      grandTotal,
      currency: currentCurrency, // حفظ العملة مع البيع
      paymentMethod,
      paymentStatus,
      employeeId,
      employeeName,
      branchId,
      branchName: branch.name,
      warehouseId,
      warehouseName: branch.warehouseIds.find(id => id === warehouseId) || 'مخزن غير معروف',
      customerId,
      customerName,
      customerPhone,
      createdAt: new Date(),
      receiptPrinted: false,
      notes: notes || '',
      amountPaid: amountPaid !== undefined ? amountPaid : grandTotal,
      remainingAmount: remainingAmount || 0,
      isDebt: isDebt || false,
      // تحويل تاريخ الاستحقاق إلى كائن Date أو حذفه إذا كان غير موجود
      ...(dueDate ? { dueDate: new Date(dueDate) } : {})
    };

    // ✅ التحقق من توفر جميع المنتجات دفعة واحدة (محسّن)
    const inventoryChecks = await Promise.all(
      items.map((item: SaleItem) =>
        getInventoryQuantityInWarehouse(item.itemId, warehouseId)
          .then((available: number) => ({ item, available }))
      )
    );

    // التحقق من النتائج
    const insufficientItems = inventoryChecks.filter(
      ({ item, available }: any) => available < item.quantity
    );

    if (insufficientItems.length > 0) {
      const errors = insufficientItems.map(
        ({ item, available }: any) =>
          `${item.name}: متوفر ${available} من ${item.quantity}`
      ).join(', ');
      throw new Error(`كمية غير كافية: ${errors}`);
    }

    // Add sale to Firestore
    const newSaleRef = await addDoc(salesCollection, {
      ...saleData,
      createdAt: serverTimestamp()
    });

    const saleId = newSaleRef.id;
    const warehouseName = branch.warehouseIds.find(id => id === warehouseId) || 'مخزن غير معروف';

    // ✅ تحديث المخزون بشكل متوازي (محسّن)
    await Promise.all(
      items.map(async (item: SaleItem) => {
        await updateInventoryQuantityInWarehouse(
          item.itemId,
          warehouseId,
          -item.quantity,
          employeeId,
          `بيع #${saleId}`
        );

        return addInventoryTransaction({
          id: '',
          type: TransactionType.SALE,
          itemId: item.itemId,
          itemName: item.name,
          itemSku: item.sku || '',
          quantity: item.quantity,
          fromWarehouseId: warehouseId,
          fromWarehouseName: warehouseName,
          createdById: employeeId,
          createdByName: employeeName,
          notes: `بيع #${saleId} - ${branch.name}`,
          timestamp: new Date()
        });
      })
    );

    // Update customer's last visit if customer is provided
    if (customerId) {
      await updateCustomerLastVisit(customerId);

      // If this is a debt sale, create a debt record using unified service
      if (isDebt && remainingAmount && remainingAmount > 0) {
        try {
          await createDebtFromSaleUnified(
            newSaleRef.id,
            customerId,
            remainingAmount,
            `دين من عملية بيع رقم ${newSaleRef.id.substring(0, 8)}`,
            employeeId,
            employeeName,
            branchId,
            branch.name,
            dueDate ? new Date(dueDate) : undefined,
            notes
          );
        } catch (debtError) {
          console.error('Error creating debt from sale:', debtError);
          // لا نريد إلغاء عملية البيع إذا فشل إنشاء الدين
          // لذلك نقوم بالتقاط الخطأ ومتابعة التنفيذ
        }
      }
    }

    // إضافة المبيعات النقدية للخزينة
    if (paymentMethod === 'cash' && amountPaid && amountPaid > 0) {
      console.log(`💰 إضافة مبيعات نقدية للخزينة: ${amountPaid} جنيه`);

      try {
        if (isLocalMode()) {
          const tx = {
            id: generateLocalId('drawer'),
            type: 'sale_payment',
            amount: amountPaid,
            description: `دفعة بيع نقدية - فاتورة ${invoiceNumber}`,
            customerId: customerId || '',
            customerName: customerName || 'عميل عام',
            employeeId, employeeName,
            branchId, branchName: branch.name,
            saleId: newSaleRef.id, invoiceNumber,
            date: new Date().toISOString().split('T')[0],
            createdAt: new Date()
          };
          const txList = getLocalList<any>(DRAWER_TX_LS_KEY);
          txList.push(tx);
          setLocalList(DRAWER_TX_LS_KEY, txList);
        } else {
          await addDoc(collection(db, 'drawerTransactions'), {
            type: 'sale_payment',
            amount: amountPaid,
            description: `دفعة بيع نقدية - فاتورة ${invoiceNumber}`,
            customerId: customerId || '',
            customerName: customerName || 'عميل عام',
            employeeId,
            employeeName,
            branchId,
            branchName: branch.name,
            saleId: newSaleRef.id,
            invoiceNumber,
            date: new Date().toISOString().split('T')[0],
            createdAt: serverTimestamp()
          });
        }

        console.log(`✅ تم إضافة ${amountPaid} جنيه للخزينة من البيع النقدي`);
      } catch (drawerError) {
        console.error('❌ خطأ في إضافة المبيعات النقدية للخزينة:', drawerError);
      }
    }

    // Get the newly created sale
    const newSaleDoc = await getDoc(newSaleRef);
    const newSaleData = newSaleDoc.data();

    return {
      id: newSaleDoc.id,
      ...newSaleData,
      createdAt: (newSaleData?.createdAt as Timestamp).toDate()
    } as Sale;
  } catch (error) {
    console.error('Error creating sale:', error);
    throw error;
  }
};

// Get sale by ID
export const getSaleById = async (saleId: string): Promise<Sale | null> => {
  try {
    if (isLocalMode()) {
      return getLocalList<Sale>(SALES_LS_KEY).find(s => s.id === saleId) || null;
    }

    const firestore = ensureFirestore();
    const saleDoc = await getDoc(doc(firestore, 'sales', saleId));

    if (saleDoc.exists()) {
      const data = saleDoc.data();
      return {
        id: saleDoc.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt
      } as Sale;
    }

    return null;
  } catch (error) {
    console.error('Error getting sale by ID:', error);
    return null;
  }
};

// Get all sales
export const getAllSales = async (limitCount: number = 100): Promise<Sale[]> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<Sale>(SALES_LS_KEY);
      return list
        .sort((a, b) => toDate(b.createdAt).getTime() - toDate(a.createdAt).getTime())
        .slice(0, limitCount);
    }

    const salesCollection = getSalesCollection();
    const q = query(
      salesCollection,
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt
      } as Sale;
    });
  } catch (error) {
    console.error('Error getting all sales:', error);
    return [];
  }
};

// Get sales by customer
export const getSalesByCustomer = async (customerId: string): Promise<Sale[]> => {
  try {
    if (isLocalMode()) {
      return getLocalList<Sale>(SALES_LS_KEY)
        .filter(s => s.customerId === customerId)
        .sort((a, b) => toDate(b.createdAt).getTime() - toDate(a.createdAt).getTime());
    }

    const salesCollection = getSalesCollection();
    const q = query(
      salesCollection,
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt
      } as Sale;
    });
  } catch (error) {
    console.error('Error getting sales by customer:', error);
    return [];
  }
};

// Update receipt printed status
export const updateReceiptPrinted = async (saleId: string, printed: boolean): Promise<void> => {
  try {
    const firestore = ensureFirestore();
    const saleRef = doc(firestore, 'sales', saleId);
    await updateDoc(saleRef, {
      receiptPrinted: printed
    });
  } catch (error) {
    console.error('Error updating receipt printed status:', error);
    throw error;
  }
};

// Update payment status
export const updateSalePaymentStatus = async (
  saleId: string,
  paymentStatus: PaymentStatus,
  paymentMethod?: PaymentMethod
): Promise<void> => {
  try {
    const firestore = ensureFirestore();
    const saleRef = doc(firestore, 'sales', saleId);

    const updateData: any = { paymentStatus };

    if (paymentMethod) {
      updateData.paymentMethod = paymentMethod;
    }

    await updateDoc(saleRef, updateData);
  } catch (error) {
    console.error('Error updating sale payment status:', error);
    throw error;
  }
};

// Get total sales for today with currency awareness
export const getTodaySalesTotal = async (): Promise<number> => {
  try {
    const salesCollection = getSalesCollection();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const startTimestamp = Timestamp.fromDate(today);
    const endTimestamp = Timestamp.fromDate(tomorrow);

    const q = query(
      salesCollection,
      where('createdAt', '>=', startTimestamp),
      where('createdAt', '<', endTimestamp)
    );

    const querySnapshot = await getDocs(q);

    // الحصول على العملة الحالية من إعدادات النظام
    let currentCurrency = 'EGP';
    try {
      const { getSystemSettings } = await import('./systemSettingsService');
      const settings = await getSystemSettings();
      currentCurrency = settings.currency || 'EGP';
    } catch (error) {
      console.warn('تعذر تحميل إعدادات العملة للتقرير:', error);
    }

    let total = 0;
    let salesCount = 0;
    let differentCurrencyCount = 0;

    querySnapshot.forEach(doc => {
      const data = doc.data();
      const saleTotal = data.grandTotal || 0;
      const saleCurrency = data.currency || currentCurrency; // افتراض العملة الحالية للمبيعات القديمة

      salesCount++;

      // إذا كانت العملة مختلفة، نسجل ذلك للمراجعة
      if (saleCurrency !== currentCurrency) {
        differentCurrencyCount++;
        console.warn(`⚠️ مبيعة بعملة مختلفة: ${saleCurrency} بدلاً من ${currentCurrency}`);
      }

      // حالياً نجمع جميع المبيعات بغض النظر عن العملة
      // في المستقبل يمكن إضافة تحويل العملة هنا
      total += saleTotal;
    });

    console.log(`💰 إجمالي مبيعات اليوم: ${total} ${currentCurrency}`);
    console.log(`📊 عدد المبيعات: ${salesCount}`);
    if (differentCurrencyCount > 0) {
      console.log(`⚠️ مبيعات بعملات مختلفة: ${differentCurrencyCount}`);
    }

    return total;
  } catch (error) {
    console.error('Error getting today sales total:', error);
    return 0;
  }
};

// Get recent sales with limit
export const getRecentSales = async (limitCount: number = 20): Promise<Sale[]> => {
  try {
    const salesCollection = getSalesCollection();
    const q = query(
      salesCollection,
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt
      } as Sale;
    });
  } catch (error) {
    console.error('Error getting recent sales:', error);
    return [];
  }
};

// Get sales summary by date range (for reports)
export const getSalesSummary = async (startDate: Date, endDate: Date): Promise<any> => {
  try {
    const salesCollection = getSalesCollection();
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);

    const q = query(
      salesCollection,
      where('createdAt', '>=', startTimestamp),
      where('createdAt', '<=', endTimestamp)
    );

    const querySnapshot = await getDocs(q);

    // Initialize summary object
    const summary = {
      totalSales: 0,
      totalRevenue: 0,
      totalDiscount: 0,
      totalTax: 0,
      salesByMethod: {} as Record<PaymentMethod, number>,
      salesByDay: {} as Record<string, number>,
      topSellingItems: [] as { itemId: string; name: string; quantity: number; total: number }[]
    };

    // Items map for tracking top selling items
    const itemsMap = new Map<string, { itemId: string; name: string; quantity: number; total: number }>();

    querySnapshot.docs.forEach(doc => {
      const data = doc.data() as Sale;

      // Add to total revenue
      summary.totalSales++;
      summary.totalRevenue += data.grandTotal || 0;
      summary.totalDiscount += data.discount || 0;
      summary.totalTax += data.tax || 0;

      // Add to sales by payment method
      const paymentMethod = data.paymentMethod;
      summary.salesByMethod[paymentMethod] = (summary.salesByMethod[paymentMethod] || 0) + (data.grandTotal || 0);

      // Add to sales by day
      const dateStr = data.createdAt instanceof Timestamp
        ? data.createdAt.toDate().toISOString().split('T')[0]
        : data.createdAt instanceof Date
          ? data.createdAt.toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0];
      summary.salesByDay[dateStr] = (summary.salesByDay[dateStr] || 0) + (data.grandTotal || 0);

      // Add to top selling items
      (data.items || []).forEach(item => {
        if (itemsMap.has(item.itemId)) {
          const existingItem = itemsMap.get(item.itemId)!;
          existingItem.quantity += item.quantity;
          existingItem.total += item.total;
        } else {
          itemsMap.set(item.itemId, {
            itemId: item.itemId,
            name: item.name,
            quantity: item.quantity,
            total: item.total
          });
        }
      });
    });

    // Convert items map to array and sort by quantity
    summary.topSellingItems = Array.from(itemsMap.values())
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 10); // Top 10 items

    return summary;
  } catch (error) {
    console.error('Error getting sales summary:', error);
    return {
      totalSales: 0,
      totalRevenue: 0,
      totalDiscount: 0,
      totalTax: 0,
      salesByMethod: {},
      salesByDay: {},
      topSellingItems: []
    };
  }
};

// Get sales by branch
export const getSalesByBranch = async (branchId: string): Promise<Sale[]> => {
  try {
    const salesCollection = getSalesCollection();
    const q = query(
      salesCollection,
      where('branchId', '==', branchId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt
      } as Sale;
    });
  } catch (error) {
    console.error('Error getting sales by branch:', error);
    return [];
  }
};

// Get sales by warehouse
export const getSalesByWarehouse = async (warehouseId: string): Promise<Sale[]> => {
  try {
    const salesCollection = getSalesCollection();
    const q = query(
      salesCollection,
      where('warehouseId', '==', warehouseId),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt
      } as Sale;
    });
  } catch (error) {
    console.error('Error getting sales by warehouse:', error);
    return [];
  }
};

// تم نقل دالة createDebtFromSale إلى debtService.ts لتوحيد إدارة الديون
// استخدم createDebtFromSaleUnified المستوردة من debtService.ts

// Check if user has access to sale
export const checkUserSaleAccess = async (userId: string, saleId: string): Promise<boolean> => {
  try {
    const user = await getUserById(userId);
    if (!user) {
      return false;
    }

    // Admin, branch manager and warehouse manager have access to all sales
    if (user.role === UserRole.ADMIN || user.role === UserRole.BRANCH_MANAGER || user.role === UserRole.WAREHOUSE_MANAGER) {
      return true;
    }

    const sale = await getSaleById(saleId);
    if (!sale) {
      return false;
    }

    // Check if user belongs to the same branch
    if (user.branchId && sale.branchId === user.branchId) {
      return true;
    }

    // Check if user created the sale
    if (sale.employeeId === userId) {
      return true;
    }

    // Check if user has access to the warehouse
    const hasWarehouseAccess = await checkUserWarehouseAccess(userId, sale.warehouseId);
    if (hasWarehouseAccess) {
      return true;
    }

    return false;
  } catch (error) {
    console.error('Error checking user sale access:', error);
    return false;
  }
};

// Get sales summary by branch
export const getSalesSummaryByBranch = async (startDate: Date, endDate: Date): Promise<any> => {
  try {
    const salesCollection = getSalesCollection();

    // Get sales between dates
    const q = query(
      salesCollection,
      where('createdAt', '>=', startDate),
      where('createdAt', '<=', endDate)
    );

    const querySnapshot = await getDocs(q);

    const branchSales: { [key: string]: { total: number, count: number } } = {};

    querySnapshot.forEach(doc => {
      const sale = doc.data() as Sale;

      if (sale.branchId) {
        if (!branchSales[sale.branchId]) {
          branchSales[sale.branchId] = {
            total: 0,
            count: 0
          };
        }

        branchSales[sale.branchId].total += sale.grandTotal;
        branchSales[sale.branchId].count += 1;
      }
    });

    return branchSales;
  } catch (error) {
    console.error('Error getting sales summary by branch:', error);
    return {};
  }
};





// الحصول على مرجع مجموعة المرتجعات
const getReturnsCollection = () => {
  return collection(ensureFirestore(), 'returns');
};

// إنشاء مرتجع كامل للفاتورة
export const createFullReturn = async (
  saleId: string,
  returnReason: string,
  refundMethod: RefundMethod,
  employeeId: string,
  employeeName: string,
  notes?: string
): Promise<SaleReturn> => {
  try {
    // الحصول على الفاتورة الأصلية
    const sale = await getSaleById(saleId);
    if (!sale) {
      throw new Error(`الفاتورة غير موجودة: ${saleId}`);
    }

    // التحقق من حالة الفاتورة
    if (sale.returnStatus === 'FULL_RETURNED') {
      throw new Error('هذه الفاتورة مرتجعة كلياً بالفعل ولا يمكن إرجاعها مرة أخرى');
    }

    // التحقق من صلاحية المستخدم للوصول إلى الفاتورة
    const hasAccess = await checkUserSaleAccess(employeeId, saleId);
    if (!hasAccess) {
      throw new Error(`ليس لديك صلاحية للوصول إلى هذه الفاتورة`);
    }

    // إنشاء عناصر المرتجع
    const returnItems: ReturnItem[] = sale.items.map(item => ({
      itemId: item.itemId,
      name: item.name,
      sku: item.sku || '',
      quantity: item.quantity,
      price: item.price,
      total: item.total,
      warehouseId: item.warehouseId,
      returnReason,
      returnQuantity: item.quantity
    }));

    // إنشاء كائن المرتجع
    const returnData: Omit<SaleReturn, 'id'> = {
      saleId,
      returnType: ReturnType.FULL,
      returnStatus: ReturnStatus.COMPLETED,
      items: returnItems,
      totalAmount: sale.grandTotal,
      returnReason,
      refundMethod,
      employeeId,
      employeeName,
      branchId: sale.branchId,
      branchName: sale.branchName,
      warehouseId: sale.warehouseId,
      warehouseName: sale.warehouseName,
      customerId: sale.customerId,
      customerName: sale.customerName,
      createdAt: new Date(),
      notes: notes || ''
    };

    // إضافة المرتجع إلى Firestore
    const returnsCollection = getReturnsCollection();
    console.log(`🔄 إنشاء مرتجع كامل للفاتورة: ${saleId}`);
    console.log(`📄 بيانات المرتجع:`, returnData);

    const newReturnRef = await addDoc(returnsCollection, {
      ...returnData,
      createdAt: serverTimestamp()
    });

    console.log(`✅ تم إنشاء المرتجع الكامل: ${newReturnRef.id}`);

    // تحديث حالة الفاتورة الأصلية
    await updateSaleReturnStatus(saleId, 'FULL_RETURNED', newReturnRef.id);

    // إعادة المنتجات إلى المخزن
    for (const item of returnItems) {
      await updateInventoryQuantityInWarehouse(
        item.itemId,
        sale.warehouseId,
        item.quantity, // إضافة الكمية (عكس عملية البيع)
        employeeId,
        `مرتجع منتج من الفاتورة #${saleId}`
      );

      // إضافة معاملة مخزون للمنتجات المرتجعة
      await addInventoryTransaction({
        id: '',
        type: TransactionType.ADJUSTMENT,
        itemId: item.itemId,
        itemName: item.name,
        itemSku: item.sku || '',
        quantity: item.quantity,
        toWarehouseId: sale.warehouseId,
        toWarehouseName: sale.warehouseName,
        createdById: employeeId,
        createdByName: employeeName,
        notes: `مرتجع منتج من الفاتورة #${saleId}`,
        timestamp: new Date()
      });
    }

    // معالجة الاسترداد حسب الطريقة المختارة
    console.log(`💳 طريقة الاسترداد: ${refundMethod}`);
    if (refundMethod === RefundMethod.CASH) {
      // استرداد نقدي - خصم من الخزينة
      console.log(`💰 بدء الاسترداد النقدي: ${sale.grandTotal} من الفرع ${sale.branchId}`);
      await updateCashRegisterForReturn(sale.branchId, sale.grandTotal, employeeId);
      console.log(`✅ تم استرداد ${sale.grandTotal} نقداً للعميل`);
    } else if (refundMethod === RefundMethod.CREDIT && sale.customerId) {
      // إضافة رصيد للعميل
      await addCustomerCreditFromReturn(
        sale.customerId,
        sale.grandTotal,
        newReturnRef.id,
        employeeId,
        employeeName,
        sale.branchId,
        sale.branchName,
        `رصيد من مرتجع كامل للفاتورة ${sale.invoiceNumber || sale.id.substring(0, 8)}`
      );
      console.log(`💳 تم إضافة ${sale.grandTotal} كرصيد للعميل`);
    }

    // الحصول على المرتجع الجديد
    const newReturnDoc = await getDoc(newReturnRef);
    const newReturnData = newReturnDoc.data();

    return {
      id: newReturnDoc.id,
      ...returnData,
      createdAt: (newReturnData?.createdAt as Timestamp)?.toDate() || new Date()
    } as SaleReturn;
  } catch (error) {
    console.error('Error creating full return:', error);
    throw error;
  }
};

// إنشاء مرتجع جزئي للفاتورة
export const createPartialReturn = async (
  saleId: string,
  returnItems: ReturnItem[],
  returnReason: string,
  refundMethod: RefundMethod,
  employeeId: string,
  employeeName: string,
  notes?: string
): Promise<SaleReturn> => {
  try {
    // الحصول على الفاتورة الأصلية
    const sale = await getSaleById(saleId);
    if (!sale) {
      throw new Error(`الفاتورة غير موجودة: ${saleId}`);
    }

    // التحقق من حالة الفاتورة
    if (sale.returnStatus === 'FULL_RETURNED') {
      throw new Error('هذه الفاتورة مرتجعة كلياً بالفعل ولا يمكن إرجاع المزيد منها');
    }

    // حساب الكميات المرتجعة سابقاً
    const previousReturns = await getSaleReturns(saleId);
    const previouslyReturnedItems: Record<string, number> = {};

    previousReturns.forEach(returnRecord => {
      returnRecord.items.forEach(item => {
        const key = item.itemId;
        previouslyReturnedItems[key] = (previouslyReturnedItems[key] || 0) + item.quantity;
      });
    });

    // التحقق من أن الكميات المطلوب إرجاعها لا تتجاوز المتاح
    for (const returnItem of returnItems) {
      const originalItem = sale.items.find(item => item.itemId === returnItem.itemId);
      if (!originalItem) {
        throw new Error(`العنصر ${returnItem.name} غير موجود في الفاتورة الأصلية`);
      }

      const previouslyReturned = previouslyReturnedItems[returnItem.itemId] || 0;
      const availableToReturn = originalItem.quantity - previouslyReturned;

      if (returnItem.quantity > availableToReturn) {
        throw new Error(`لا يمكن إرجاع ${returnItem.quantity} من ${returnItem.name}. المتاح للإرجاع: ${availableToReturn}`);
      }
    }

    // التحقق من صلاحية المستخدم للوصول إلى الفاتورة
    const hasAccess = await checkUserSaleAccess(employeeId, saleId);
    if (!hasAccess) {
      throw new Error(`ليس لديك صلاحية للوصول إلى هذه الفاتورة`);
    }

    // التحقق من صحة العناصر المرتجعة
    for (const returnItem of returnItems) {
      const originalItem = sale.items.find(item => item.itemId === returnItem.itemId);
      if (!originalItem) {
        throw new Error(`العنصر غير موجود في الفاتورة الأصلية: ${returnItem.name}`);
      }

      if ((returnItem.returnQuantity || 0) > originalItem.quantity) {
        throw new Error(`كمية المرتجع أكبر من الكمية الأصلية للعنصر: ${returnItem.name}`);
      }
    }

    // حساب إجمالي مبلغ المرتجع
    const totalAmount = returnItems.reduce((total, item) => {
      const originalItem = sale.items.find(i => i.itemId === item.itemId);
      if (!originalItem) return total;

      const itemPrice = originalItem.price;
      return total + (itemPrice * (item.returnQuantity || 0));
    }, 0);

    // إنشاء كائن المرتجع
    const returnData: Omit<SaleReturn, 'id'> = {
      saleId,
      returnType: ReturnType.PARTIAL,
      returnStatus: ReturnStatus.COMPLETED,
      items: returnItems,
      totalAmount,
      returnReason,
      refundMethod,
      employeeId,
      employeeName,
      branchId: sale.branchId,
      branchName: sale.branchName,
      warehouseId: sale.warehouseId,
      warehouseName: sale.warehouseName,
      customerId: sale.customerId,
      customerName: sale.customerName,
      createdAt: new Date(),
      notes: notes || ''
    };

    // إضافة المرتجع إلى Firestore
    const returnsCollection = getReturnsCollection();
    console.log(`🔄 إنشاء مرتجع جزئي للفاتورة: ${saleId}`);
    console.log(`📄 بيانات المرتجع الجزئي:`, returnData);

    const newReturnRef = await addDoc(returnsCollection, {
      ...returnData,
      createdAt: serverTimestamp()
    });

    console.log(`✅ تم إنشاء المرتجع الجزئي: ${newReturnRef.id}`);

    // تحديث حالة الفاتورة الأصلية
    await updateSaleReturnStatus(saleId, 'PARTIAL_RETURNED', newReturnRef.id);

    // إعادة المنتجات إلى المخزن
    for (const item of returnItems) {
      await updateInventoryQuantityInWarehouse(
        item.itemId,
        sale.warehouseId,
        item.returnQuantity || 0, // إضافة الكمية المرتجعة فقط
        employeeId,
        `مرتجع جزئي للمنتج من الفاتورة #${saleId}`
      );

      // إضافة معاملة مخزون للمنتجات المرتجعة
      await addInventoryTransaction({
        id: '',
        type: TransactionType.ADJUSTMENT,
        itemId: item.itemId,
        itemName: item.name,
        itemSku: item.sku || '',
        quantity: item.returnQuantity || 0,
        toWarehouseId: sale.warehouseId,
        toWarehouseName: sale.warehouseName,
        createdById: employeeId,
        createdByName: employeeName,
        notes: `مرتجع جزئي للمنتج من الفاتورة #${saleId}`,
        timestamp: new Date()
      });
    }

    // معالجة الاسترداد حسب الطريقة المختارة
    console.log(`💳 طريقة الاسترداد الجزئي: ${refundMethod}`);
    if (refundMethod === RefundMethod.CASH) {
      // استرداد نقدي - خصم من الخزينة
      console.log(`💰 بدء الاسترداد النقدي الجزئي: ${totalAmount} من الفرع ${sale.branchId}`);
      await updateCashRegisterForReturn(sale.branchId, totalAmount, employeeId);
      console.log(`✅ تم استرداد ${totalAmount} نقداً للعميل`);
    } else if (refundMethod === RefundMethod.CREDIT && sale.customerId) {
      // إضافة رصيد للعميل
      await addCustomerCreditFromReturn(
        sale.customerId,
        totalAmount,
        newReturnRef.id,
        employeeId,
        employeeName,
        sale.branchId,
        sale.branchName,
        `رصيد من مرتجع جزئي للفاتورة ${sale.invoiceNumber || sale.id.substring(0, 8)}`
      );
      console.log(`💳 تم إضافة ${totalAmount} كرصيد للعميل`);
    }

    // الحصول على المرتجع الجديد
    const newReturnDoc = await getDoc(newReturnRef);
    const newReturnData = newReturnDoc.data();

    return {
      id: newReturnDoc.id,
      ...returnData,
      createdAt: (newReturnData?.createdAt as Timestamp)?.toDate() || new Date()
    } as SaleReturn;
  } catch (error) {
    console.error('Error creating partial return:', error);
    throw error;
  }
};

// الحصول على مرتجعات الفاتورة
export const getSaleReturns = async (saleId: string): Promise<SaleReturn[]> => {
  try {
    console.log(`🔍 البحث عن مرتجعات للفاتورة: ${saleId}`);

    if (isLocalMode()) {
      return getLocalList<SaleReturn>(RETURNS_LS_KEY)
        .filter(r => r.saleId === saleId)
        .sort((a, b) => toDate(b.createdAt).getTime() - toDate(a.createdAt).getTime());
    }

    const returnsCollection = getReturnsCollection();

    // محاولة الاستعلام مع الفهرس أولاً
    try {
      const q = query(
        returnsCollection,
        where('saleId', '==', saleId),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      console.log(`📊 تم العثور على ${querySnapshot.docs.length} مرتجع للفاتورة ${saleId} (مع الفهرس)`);

      const returns = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log(`📄 مرتجع ${doc.id}:`, data);
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt
        } as SaleReturn;
      });

      return returns;
    } catch (indexError) {
      const errorMessage = indexError instanceof Error ? indexError.message : String(indexError);
      console.warn('⚠️ الفهرس غير متوفر، استخدام استعلام بديل:', errorMessage);

      // استعلام بديل بدون ترتيب
      const simpleQuery = query(
        returnsCollection,
        where('saleId', '==', saleId)
      );
      const querySnapshot = await getDocs(simpleQuery);
      console.log(`📊 تم العثور على ${querySnapshot.docs.length} مرتجع للفاتورة ${saleId} (بدون ترتيب)`);

      const returns = querySnapshot.docs.map(doc => {
        const data = doc.data();
        console.log(`📄 مرتجع ${doc.id}:`, data);
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt
        } as SaleReturn;
      });

      // ترتيب يدوي حسب التاريخ
      returns.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });

      return returns;
    }
  } catch (error) {
    console.error('❌ خطأ في جلب مرتجعات الفاتورة:', error);
    return [];
  }
};

// الحصول على جميع المرتجعات
export const getAllReturns = async (limitCount: number = 100): Promise<SaleReturn[]> => {
  try {
    console.log(`🔍 جلب جميع المرتجعات (الحد الأقصى: ${limitCount})`);

    if (isLocalMode()) {
      return getLocalList<SaleReturn>(RETURNS_LS_KEY)
        .sort((a, b) => toDate(b.createdAt).getTime() - toDate(a.createdAt).getTime())
        .slice(0, limitCount);
    }

    const returnsCollection = getReturnsCollection();

    // محاولة الاستعلام مع الفهرس أولاً
    try {
      const q = query(
        returnsCollection,
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
      const querySnapshot = await getDocs(q);
      console.log(`📊 تم العثور على ${querySnapshot.docs.length} مرتجع إجمالي (مع الفهرس)`);

      const returns = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt
        } as SaleReturn;
      });

      if (returns.length > 0) {
        console.log(`📄 أول مرتجع:`, returns[0]);
      }

      return returns;
    } catch (indexError) {
      const errorMessage = indexError instanceof Error ? indexError.message : String(indexError);
      console.warn('⚠️ الفهرس غير متوفر، جلب جميع المرتجعات بدون ترتيب:', errorMessage);

      // استعلام بديل بدون ترتيب
      const simpleQuery = query(returnsCollection, limit(limitCount));
      const querySnapshot = await getDocs(simpleQuery);
      console.log(`📊 تم العثور على ${querySnapshot.docs.length} مرتجع إجمالي (بدون ترتيب)`);

      const returns = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : data.createdAt
        } as SaleReturn;
      });

      // ترتيب يدوي حسب التاريخ
      returns.sort((a, b) => {
        const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      });

      if (returns.length > 0) {
        console.log(`📄 أول مرتجع (بعد الترتيب):`, returns[0]);
      }

      return returns;
    }
  } catch (error) {
    console.error('❌ خطأ في جلب جميع المرتجعات:', error);
    return [];
  }
};

/**
 * الحصول على الكميات المتاحة للإرجاع لكل عنصر في الفاتورة
 */
export const getAvailableReturnQuantities = async (saleId: string): Promise<Record<string, {
  originalQuantity: number;
  returnedQuantity: number;
  availableQuantity: number;
  itemName: string;
}>> => {
  try {
    console.log(`🔍 حساب الكميات المتاحة للإرجاع للفاتورة: ${saleId}`);

    const sale = await getSaleById(saleId);
    if (!sale) {
      throw new Error(`الفاتورة غير موجودة: ${saleId}`);
    }

    const previousReturns = await getSaleReturns(saleId);

    const returnedQuantities: Record<string, number> = {};
    previousReturns.forEach(returnRecord => {
      returnRecord.items.forEach(item => {
        const key = item.itemId;
        returnedQuantities[key] = (returnedQuantities[key] || 0) + item.quantity;
      });
    });

    const availableQuantities: Record<string, {
      originalQuantity: number;
      returnedQuantity: number;
      availableQuantity: number;
      itemName: string;
    }> = {};

    sale.items.forEach(item => {
      const returnedQty = returnedQuantities[item.itemId] || 0;
      const availableQty = item.quantity - returnedQty;

      availableQuantities[item.itemId] = {
        originalQuantity: item.quantity,
        returnedQuantity: returnedQty,
        availableQuantity: Math.max(0, availableQty),
        itemName: item.name
      };
    });

    return availableQuantities;
  } catch (error) {
    console.error('❌ خطأ في حساب الكميات المتاحة للإرجاع:', error);
    throw error;
  }
};

// دالة تشخيص للتحقق من المرتجعات
export const debugReturns = async () => {
  try {
    console.log('🔧 بدء تشخيص المرتجعات...');

    // التحقق من مجموعة returns
    const returnsSnapshot = await getDocs(collection(ensureFirestore(), 'returns'));
    console.log(`📊 عدد المرتجعات في مجموعة 'returns': ${returnsSnapshot.docs.length}`);

    // التحقق من مجموعة sale_returns
    const saleReturnsSnapshot = await getDocs(collection(ensureFirestore(), 'sale_returns'));
    console.log(`📊 عدد المرتجعات في مجموعة 'sale_returns': ${saleReturnsSnapshot.docs.length}`);

    // عرض أول 3 مرتجعات من كل مجموعة
    if (returnsSnapshot.docs.length > 0) {
      console.log('📄 أول 3 مرتجعات من مجموعة returns:');
      returnsSnapshot.docs.slice(0, 3).forEach((doc, index) => {
        console.log(`${index + 1}. ${doc.id}:`, doc.data());
      });
    }

    if (saleReturnsSnapshot.docs.length > 0) {
      console.log('📄 أول 3 مرتجعات من مجموعة sale_returns:');
      saleReturnsSnapshot.docs.slice(0, 3).forEach((doc, index) => {
        console.log(`${index + 1}. ${doc.id}:`, doc.data());
      });
    }

    console.log('✅ انتهى تشخيص المرتجعات');
  } catch (error) {
    console.error('❌ خطأ في تشخيص المرتجعات:', error);
  }
};

/**
 * إنشاء معاملة بيع لدفعة صيانة نقدية
 * لتسجيلها في نظام نقاط البيع وتقرير الدرج
 */
export const createMaintenancePaymentSale = async (
  maintenanceId: string,
  customerName: string,
  amount: number,
  employeeId: string,
  employeeName: string,
  branchId: string,
  warehouseId: string,
  notes?: string
): Promise<Sale> => {
  try {
    // إنشاء عنصر وهمي لدفعة الصيانة
    const maintenanceItem: SaleItem = {
      itemId: 'maintenance-payment',
      name: `دفعة صيانة - ${customerName}`,
      sku: `MAINT-${maintenanceId.substring(0, 8)}`,
      quantity: 1,
      price: amount,
      total: amount,
      warehouseId: warehouseId
    };

    // إنشاء معاملة بيع
    const sale = await createSale(
      [maintenanceItem],
      amount, // المجموع الفرعي
      0, // خصم
      0, // ضريبة
      'cash', // طريقة الدفع نقدي
      'paid', // حالة الدفع مكتملة
      employeeId,
      employeeName,
      branchId,
      warehouseId,
      undefined, // معرف العميل (غير مطلوب)
      customerName,
      notes || `دفعة صيانة للطلب ${maintenanceId.substring(0, 8)}`,
      amount, // المبلغ المدفوع
      0, // المبلغ المتبقي
      false // ليس دين
    );

    console.log(`Maintenance payment sale created: ${sale.id} for amount ${amount}`);
    return sale;
  } catch (error) {
    console.error('Error creating maintenance payment sale:', error);
    throw error;
  }
};

/**
 * إنشاء سجل إرجاع منتجات
 */
export const createReturn = async (returnData: Omit<ReturnRecord, 'id' | 'createdAt'>): Promise<ReturnRecord> => {
  try {
    const returnRecord = {
      ...returnData,
      createdAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'returns'), returnRecord);

    // تحديث المخزون لكل منتج مُرجع
    for (const item of returnData.items) {
      await updateInventoryForReturn(item.itemId, item.warehouseId, item.quantity);
    }

    // تحديث الخزينة (خصم المبلغ المُرجع)
    await updateCashRegisterForReturn(returnData.branchId, returnData.totalAmount, returnData.employeeId);

    console.log(`Return created: ${docRef.id} for amount ${returnData.totalAmount}`);

    return {
      id: docRef.id,
      ...returnData,
      createdAt: returnRecord.createdAt.toDate()
    };
  } catch (error) {
    console.error('Error creating return:', error);
    throw error;
  }
};

/**
 * تحديث المخزون عند الإرجاع
 */
const updateInventoryForReturn = async (itemId: string, warehouseId: string, quantity: number) => {
  try {
    const inventoryQuery = query(
      collection(db, 'inventory'),
      where('itemId', '==', itemId),
      where('warehouseId', '==', warehouseId)
    );

    const querySnapshot = await getDocs(inventoryQuery);

    if (!querySnapshot.empty) {
      const inventoryDoc = querySnapshot.docs[0];
      const currentQuantity = inventoryDoc.data().quantity || 0;

      await updateDoc(inventoryDoc.ref, {
        quantity: currentQuantity + quantity, // إضافة الكمية المُرجعة
        updatedAt: Timestamp.now()
      });

      console.log(`Inventory updated for item ${itemId}: +${quantity}`);
    }
  } catch (error) {
    console.error('Error updating inventory for return:', error);
    throw error;
  }
};

/**
 * تحديث الخزينة عند الإرجاع
 */
export const updateCashRegisterForReturn = async (branchId: string, amount: number, employeeId: string) => {
  try {
    console.log(`🏦 تحديث الخزينة للإرجاع - الفرع: ${branchId}, المبلغ: ${amount}, الموظف: ${employeeId}`);

    const now = new Date();
    const transaction = {
      id: '',
      type: 'return',
      amount: -Math.abs(amount),
      description: `إرجاع منتجات - خصم ${amount} جنيه من الخزينة`,
      branchId,
      employeeId,
      date: now.toISOString().split('T')[0],
      createdAt: now
    };

    if (isLocalMode()) {
      const tx = { ...transaction, id: generateLocalId('drawer') };
      const list = getLocalList<any>(DRAWER_TX_LS_KEY);
      list.push(tx);
      setLocalList(DRAWER_TX_LS_KEY, list);
      return;
    }

    transaction.createdAt = Timestamp.now() as any;
    const docRef = await addDoc(collection(db, 'drawerTransactions'), transaction);
    console.log(`✅ تم خصم ${amount} من الخزينة للإرجاع, معرف المعاملة: ${docRef.id}`);
  } catch (error) {
    console.error('❌ خطأ في تحديث الخزينة للإرجاع:', error);
    throw error;
  }
};

/**
 * تحديث حالة الفاتورة الأصلية لتظهر أنها مرتجعة
 */
export const updateSaleReturnStatus = async (saleId: string, returnStatus: 'FULL_RETURNED' | 'PARTIAL_RETURNED', returnId: string) => {
  try {
    console.log(`📝 تحديث حالة الفاتورة ${saleId} إلى ${returnStatus}`);

    if (isLocalMode()) {
      const list = getLocalList<Sale>(SALES_LS_KEY);
      const idx = list.findIndex(s => s.id === saleId);
      if (idx < 0) {
        console.warn(`⚠️ الفاتورة ${saleId} غير موجودة`);
        return;
      }
      const currentSale = list[idx];
      const existingReturns = (currentSale as any).returns || [];
      const updatedReturns = [...existingReturns, returnId];
      let newStatus = returnStatus;
      if (existingReturns.length > 0 && returnStatus === 'PARTIAL_RETURNED') {
        newStatus = 'PARTIAL_RETURNED';
      }
      list[idx] = {
        ...currentSale,
        returnStatus: newStatus,
        returns: updatedReturns,
        lastReturnDate: new Date(),
        updatedAt: new Date()
      } as any;
      setLocalList(SALES_LS_KEY, list);
      console.log(`✅ تم تحديث حالة الفاتورة ${saleId} إلى ${newStatus}`);
      return;
    }

    const saleRef = doc(ensureFirestore(), 'sales', saleId);
    const saleDoc = await getDoc(saleRef);

    if (!saleDoc.exists()) {
      console.warn(`⚠️ الفاتورة ${saleId} غير موجودة`);
      return;
    }

    const currentData = saleDoc.data();
    const existingReturns = currentData.returns || [];

    const updatedReturns = [...existingReturns, returnId];

    let newStatus = returnStatus;
    if (existingReturns.length > 0 && returnStatus === 'PARTIAL_RETURNED') {
      newStatus = 'PARTIAL_RETURNED';
    }

    await updateDoc(saleRef, {
      returnStatus: newStatus,
      returns: updatedReturns,
      lastReturnDate: Timestamp.now(),
      updatedAt: Timestamp.now()
    });

    console.log(`✅ تم تحديث حالة الفاتورة ${saleId} إلى ${newStatus}`);
  } catch (error) {
    console.error('❌ خطأ في تحديث حالة الفاتورة:', error);
    throw error;
  }
};

/**
 * الحصول على جميع المرتجعات
 */
export const getReturns = async (branchId?: string): Promise<ReturnRecord[]> => {
  try {
    if (isLocalMode()) {
      const list = getLocalList<any>(RETURNS_LS_KEY);
      const filtered = branchId ? list.filter(r => r.branchId === branchId) : list;
      return filtered
        .map(r => ({
          id: r.id,
          saleId: r.saleId,
          customerId: r.customerId,
          customerName: r.customerName,
          employeeId: r.employeeId,
          employeeName: r.employeeName,
          branchId: r.branchId,
          warehouseId: r.warehouseId,
          items: r.items || [],
          totalAmount: r.totalAmount,
          reason: r.reason,
          status: r.status,
          createdAt: toDate(r.createdAt)
        } as ReturnRecord))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }

    let querySnapshot;
    if (branchId) {
      const q = query(collection(db, 'returns'), where('branchId', '==', branchId));
      querySnapshot = await getDocs(q);
    } else {
      querySnapshot = await getDocs(collection(db, 'returns'));
    }
    const returns: ReturnRecord[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      returns.push({
        id: doc.id,
        saleId: data.saleId,
        customerId: data.customerId,
        customerName: data.customerName,
        employeeId: data.employeeId,
        employeeName: data.employeeName,
        branchId: data.branchId,
        warehouseId: data.warehouseId,
        items: data.items,
        totalAmount: data.totalAmount,
        reason: data.reason,
        status: data.status,
        createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt)
      });
    });

    return returns.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  } catch (error) {
    console.error('Error getting returns:', error);
    return [];
  }
};